import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"
import React, { useState, useRef, useEffect } from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const FAQSection = ({ section, theme, highlightedField }) => {
  const [expanded, setExpanded] = useState({})
  const [animatingItems, setAnimatingItems] = useState({})
  const timeoutRefs = useRef({})

  const isHighlighted = highlightedField === `section-${section.id}`

  // Get FAQ data from section settings
  const faqItems = section?.settings?.faqs || []
  const sectionSettings = section?.settings || {}

  // Get theme colors with fallback generation
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    if (!colors.bgMedium || !colors.bgExtraLight) {
      const primaryColor = colors.primary || "#BD654F"
      const generated = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generated.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generated.bgExtraLight
      colors.bgLight = colors.bgLight || generated.bgLight
    }

    return colors
  }

  const themeColors = getThemeColors()
  const baseBackgroundColor = themeColors.bgLight || theme?.colors?.bgLight || "#FFFFFF"
  const backgroundColor = sectionSettings.showBackgroundColor
    ? sectionSettings.backgroundColor || baseBackgroundColor
    : baseBackgroundColor
  const textColor = sectionSettings.textColor || theme?.colors?.textDark || "#1F2937"
  const accentColor = theme?.colors?.primary || "#0d9da4"

  useEffect(
    () => () => {
      Object.values(timeoutRefs.current).forEach(clearTimeout)
    },
    []
  )

  const handleToggle = (question) => {
    setExpanded((prev) => {
      const newExpanded = { ...prev, [question]: !prev[question] }
      if (!newExpanded[question]) {
        setAnimatingItems((prev) => ({ ...prev, [question]: true }))
        timeoutRefs.current[question] = setTimeout(() => {
          setAnimatingItems((prev) => ({ ...prev, [question]: false }))
        }, 300)
      }
      return newExpanded
    })
  }

  // Always render the section, even if no FAQs

  return (
    <section className={twMerge("py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")} style={{ backgroundColor }}>
      <AnimatedSection className="mx-auto max-w-4xl px-4">
        <div className="space-y-8">
          {section.headline && (
            <h2
              className="text-center"
              style={{
                fontFamily: theme?.fonts?.heading || "serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                fontSize: "40px",
                lineHeight: "48px",
                color: textColor
              }}>
              {section.headline}
            </h2>
          )}
          {section.subheadline && (
            <p
              className="text-center text-lg lg:text-xl"
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: textColor,
                opacity: 0.8
              }}>
              {section.subheadline}
            </p>
          )}

          <div className="space-y-6">
            {faqItems && faqItems.length > 0 ? (
              faqItems.map((item, index) => (
                <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                  <div
                    className="flex cursor-pointer items-center justify-between gap-2"
                    onClick={() => handleToggle(item.question)}>
                    <div
                      className="flex-1 text-xl font-bold"
                      style={{
                        fontFamily: theme?.fonts?.heading || "serif",
                        fontWeight: theme?.fonts?.headingWeight || "700",
                        color: textColor
                      }}>
                      {item.question}
                    </div>
                    {expanded[item.question] || animatingItems[item.question] ? (
                      <ChevronUpIcon className="h-6 w-6 flex-none" style={{ color: accentColor }} />
                    ) : (
                      <ChevronDownIcon className="h-6 w-6 flex-none" style={{ color: accentColor }} />
                    )}
                  </div>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      expanded[item.question] ? "max-h-96" : "max-h-0"
                    }`}>
                    <style>{`
                      .faq-section-content {
                        text-align: initial !important;
                      }
                      .faq-section-content ul {
                        list-style-type: disc !important;
                        margin-left: 1.5rem !important;
                        margin-bottom: 1rem !important;
                      }
                      .faq-section-content ol {
                        list-style-type: decimal !important;
                        margin-left: 1.5rem !important;
                        margin-bottom: 1rem !important;
                      }
                      .faq-section-content li {
                        margin-bottom: 0.5rem !important;
                        display: list-item !important;
                      }
                      .faq-section-content .ql-align-right {
                        text-align: right !important;
                      }
                      .faq-section-content .ql-align-center {
                        text-align: center !important;
                      }
                      .faq-section-content .ql-align-justify {
                        text-align: justify !important;
                      }
                      .faq-section-content .ql-align-left {
                        text-align: left !important;
                      }
                      .faq-section-content a {
                        color: ${theme?.colors?.primary || "#BD654F"} !important;
                        text-decoration: underline !important;
                      }
                      .faq-section-content a:hover {
                        color: ${theme?.colors?.primaryDark || "#A55A45"} !important;
                        text-decoration: underline !important;
                      }
                      .faq-section-content p {
                        margin-bottom: 1rem !important;
                        margin-top: 0 !important;
                      }
                      .faq-section-content p:first-child {
                        margin-top: 0 !important;
                      }
                      .faq-section-content p:last-child {
                        margin-bottom: 0 !important;
                      }
                      .faq-section-content strong,
                      .faq-section-content b {
                        font-weight: ${theme?.fonts?.paragraphBoldWeight || "600"} !important;
                      }
                      .faq-section-content em,
                      .faq-section-content i {
                        font-style: italic !important;
                      }
                      .faq-section-content u {
                        text-decoration: underline !important;
                      }
                      .faq-section-content h1 {
                        font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                        font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                        font-size: 56px !important;
                        line-height: 72px !important;
                        margin-bottom: 1rem !important;
                        margin-top: 1.5rem !important;
                        color: ${textColor} !important;
                      }
                      .faq-section-content h2 {
                        font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                        font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                        font-size: 40px !important;
                        line-height: 48px !important;
                        margin-bottom: 1rem !important;
                        margin-top: 1.5rem !important;
                        color: ${textColor} !important;
                      }
                      .faq-section-content h3 {
                        font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                        font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                        font-size: 27px !important;
                        line-height: 40px !important;
                        margin-bottom: 1rem !important;
                        margin-top: 1.5rem !important;
                        color: ${textColor} !important;
                      }
                      .faq-section-content h4 {
                        font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                        font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                        font-size: 20px !important;
                        line-height: 32px !important;
                        margin-bottom: 0.75rem !important;
                        margin-top: 1.25rem !important;
                        color: ${textColor} !important;
                      }
                      .faq-section-content h5 {
                        font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                        font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                        font-size: 18px !important;
                        line-height: 28px !important;
                        margin-bottom: 0.5rem !important;
                        margin-top: 1rem !important;
                        color: ${textColor} !important;
                      }
                      .faq-section-content h6 {
                        font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                        font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                        font-size: 16px !important;
                        line-height: 24px !important;
                        margin-bottom: 0.5rem !important;
                        margin-top: 1rem !important;
                        color: ${textColor} !important;
                      }
                      @media (max-width: 768px) {
                        .faq-section-content h1 {
                          font-size: 32px !important;
                          line-height: 48px !important;
                        }
                        .faq-section-content h2 {
                          font-size: 27px !important;
                          line-height: 32px !important;
                        }
                        .faq-section-content h3 {
                          font-size: 22px !important;
                          line-height: 32px !important;
                        }
                        .faq-section-content h4 {
                          font-size: 18px !important;
                          line-height: 24px !important;
                        }
                        .faq-section-content h5 {
                          font-size: 16px !important;
                          line-height: 24px !important;
                        }
                        .faq-section-content h6 {
                          font-size: 14px !important;
                          line-height: 20px !important;
                        }
                      }
                    `}</style>
                    <div
                      className="faq-section-content prose prose-lg max-w-none pt-4 text-[20px]" // eslint-disable-line tailwindcss/no-custom-classname
                      style={{
                        fontFamily: theme?.fonts?.paragraph || "sans-serif",
                        color: textColor
                      }}
                      dangerouslySetInnerHTML={{ __html: item.answer }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center">
                <p
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "sans-serif",
                    color: textColor,
                    opacity: 0.6
                  }}>
                  No frequently asked questions have been added yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </AnimatedSection>
    </section>
  )
}

export default FAQSection
